import { AttentionCard } from './AttentionCard';
import { Metrics } from './Metrics';
import { Visitors } from './Visitors';
import { DashboardChart } from './DashboardChart';
import { ExperimentsOverviewTable } from './ExperimentsOverviewTable';
import { ExperimentsOverviewTableMobile } from './ExperimentsOverviewTableMobile';
import { overviewExperiments } from './overview-experiments';
import { GetStartedPanel } from './GetStartedPanel';
import { WaitingForVisitorsPanel } from './WaitingForVisitorsPanel';
import { waitingExperiments } from './home-states';
import { useNow } from './use-now';
import { FlaskConical, Globe, Users } from 'lucide-react';
import { Panel } from '../ConsolePanel';
import { FirstRunLink, FirstRunState } from '../FirstRunState';
import { useAuth } from 'hooks/useAuth';
import { useOrganizations } from '@/state/useOrganizations';
import { useWebsites } from '@/state/useWebsites';
import { useExperimentsList } from '@/state/experiments/useExperimentsList';
import { useDashboard } from '@/state/useDashboard';
import { useAttention } from '@/state/useAttention';

export const HomeScreen = () => {
  const { user } = useAuth();
  const { organizations, organization } = useOrganizations();
  const { website, websites } = useWebsites();
  const dashboardManager = useDashboard(website?.id);
  const experimentsManager = useExperimentsList();
  const attention = useAttention(website?.id);
  // Minute resolution is all "started 12 minutes ago" and the one-hour warning need.
  const now = useNow(60_000);
  if (!user || (!organizations && !organization)) return null;

  // Onboarding gives a new account a website, so this is an older account with none,
  // or an agency with no client selected.
  if (websites && websites.length === 0 && user.company.type === 'Agency') {
    // An agency's websites belong to its clients, so with none (or the last one removed) the
    // first step is a client, not a website.
    return (
      <FirstRunState
        icon={Users}
        title="Add a client to get started"
        description="Each client you run tests for has its own websites and results, and is covered by your plan. Add a client, then add its website."
        action={
          <FirstRunLink to="/clients" state={{ create: true }}>
            Add a client
          </FirstRunLink>
        }
      />
    );
  }

  if (websites && websites.length === 0) {
    return (
      <FirstRunState
        icon={Globe}
        title="Add a website to get started"
        description="Pertento runs experiments and surveys on your websites. Add the address of the site you want to test, then put one line of script in its pages."
        action={
          <FirstRunLink to="/websites/list" state={{ create: true }}>
            Add a website
          </FirstRunLink>
        }
      />
    );
  }

  // A website with no experiment has nothing to chart; this says what the dashboard will show.
  if (
    experimentsManager.hasWebsite &&
    !experimentsManager.isLoading &&
    !experimentsManager.isError &&
    experimentsManager.totalCount === 0
  ) {
    return (
      <FirstRunState
        icon={FlaskConical}
        title="Run your first experiment"
        description="This dashboard shows how your experiments are doing: conversions, revenue and sessions over time, and anything that needs your attention. It fills in as soon as an experiment is running."
        action={<FirstRunLink to="/experiments/library">Create an experiment</FirstRunLink>}
      >
        Make sure the Pertento snippet is on your site first. You can find it under Websites.
      </FirstRunState>
    );
  }

  const { dashboardStats } = dashboardManager;
  const chartData = dashboardStats?.chartData;
  const currencyCode = dashboardStats?.currencyCode ?? null;
  const allExperiments = experimentsManager.experiments ?? [];
  const experiments = overviewExperiments(allExperiments);
  const waiting = waitingExperiments(allExperiments, now);
  // No website and no experiment returned a full first-run screen above, so this is a website
  // with drafts but nothing running. With websites but none picked, the scope picker is the
  // next move and the attention card already says so. Not on a failed load either, where an
  // empty list would read as a brand new account.
  const showGetStarted =
    !!website && !experimentsManager.isLoading && !experimentsManager.isError && experiments.length === 0;

  return (
    <div className="flex flex-col gap-3.5">
      <AttentionCard items={attention.items} isLoading={attention.isLoading} isError={attention.isError} />
      {!!website && waiting.length > 0 && <WaitingForVisitorsPanel waiting={waiting} websiteId={website.id} />}
      {showGetStarted && <GetStartedPanel websiteId={website?.id} experiments={allExperiments} />}
      <Metrics dashboardManager={dashboardManager} counts={experimentsManager.counts} />
      <Visitors dashboardManager={dashboardManager} conversionEvent={website?.conversionEvent} />
      {Array.isArray(chartData) && chartData.length > 0 && (
        <Panel title="Overview" description="Conversions, Revenue, and Sessions over time" tourAnchor="home-chart">
          <div className="h-96">
            <DashboardChart data={chartData} currencyCode={currencyCode} />
          </div>
        </Panel>
      )}
      {experiments.length > 0 && (
        <Panel
          title="Experiment Overview"
          description="Running and deployed experiments"
          tourAnchor="home-experiments"
          bodyClassName="p-0"
        >
          <div className="hidden lg:block">
            <ExperimentsOverviewTable experiments={experiments} />
          </div>
          <div className="lg:hidden">
            <ExperimentsOverviewTableMobile
              experiments={experiments}
              view={experimentsManager.view}
              sort={experimentsManager.sort}
            />
          </div>
        </Panel>
      )}
    </div>
  );
};
